import "overlayscrollbars/overlayscrollbars.css";
import "@glidejs/glide/dist/css/glide.core.min.css";
import "@glidejs/glide/dist/css/glide.theme.min.css";
import "@tarekraafat/autocomplete.js/dist/css/autoComplete.css";

import lifecycle from "page-lifecycle";
import { $windowUtils } from "./utils/Window.js";
import { $observer } from "./utils/IntersectionObserver.js";
import { AnimatedRoutes } from "./utils/AnimatedRoutes.js";
import { SseClient } from "./utils/ServerSentEvents.js";
import { $async } from "./utils/Async.js";

export function packageLoader() {
  return {
    /**
     * Loads the dotlottie-web package.
     * @returns {Promise} A promise that resolves with the dotlottie-web module.
     */
    dotlottie: () => import("@lottiefiles/dotlottie-web"),

    /**
     * Loads the AudioService utility.
     * @returns {Promise} A promise that resolves with the AudioService module.
     */
    audioService: async () => {
      const { audioService } = await import("./utils/AudioService.js");
      return audioService;
    },

    /**
     * Loads the GSAP (GreenSock Animation Platform) package.
     * @returns {Promise} A promise that resolves with the GSAP module.
     */
    gsap: () => import("gsap"),

    /**
     * Loads the Motion.js package.
     * @returns {Promise} A promise that resolves with the Motion module.
     */
    motion: () => import("motion"),

    /**
     * Loads the StarHover util.
     * @returns {Promise} A promise that resolves with the StarHover module.
     */
    starHover: async () => {
      const { StarHover } = await import("./utils/StarHover.js");
      return new StarHover();
    },

    /**
     * Loads animation utils (Motion.js Platform) package.
     * @returns {Promise} A promise that resolves with the utils module.
     */
    animations: () => import("./utils/Animations.js"),

    /**
     * Loads nav manager utils.
     * @returns {Promise} A promise that resolves with the nav manager module.
     */
    navAnimator: async (elementOrSelector) => {
      const { NavAnimator } = await import("./utils/NavAnimator.js");
      return new NavAnimator({ navElement: elementOrSelector });
    },

    /**
     * Loads marquee utils.
     * @returns {Promise} A promise that resolves with the utils module.
     */
    gestureCarousel: async (elementOrSelector) => {
      const { GestureCarousel } = await import("./utils/GestureCarousel.js");
      return new GestureCarousel(elementOrSelector);
    },

    /**
     * Loads marquee utils.
     * @returns {Promise} A promise that resolves with the utils module.
     */
    marquee: async (elementOrSelector, text, clickCallbackHref = null) => {
      const { Marquee } = await import("./utils/Marquee.js");
      return new Marquee(elementOrSelector, text, clickCallbackHref);
    },

    /**
     * Initializes OverlayScrollbars on the document body.
     * @returns {Promise} A promise that resolves with the initialized OverlayScrollbars instance.
     */
    overlayscrollbars: () => import("overlayscrollbars"), //resolver(OverlayScrollbars(document.body, {}))),

    /**
     * Loads the Flowbite UI component library.
     * @returns {Promise} A promise that resolves with the Flowbite module.
     */
    flowbite: () => import("flowbite"),

    /**
     * Loads the EventGroup utility.
     * @returns {Promise} A promise that resolves with the EventGroup module.
     */
    eventGroup: () => import("./utils/EventGroup.js"),

    /**
     * Loads the Async utility.
     * @returns {Promise} A promise that resolves with the Async module.
     */
    async: () => import("./utils/Async.js"),

    /**
     * Loads the Tempo utility from Formkit.
     * @returns {Promise} A promise that resolves with the Tempo module.
     */
    tempo: () => import("@formkit/tempo"),

    /**
     * Resolves the window utilities instance.
     * @returns {Promise} A promise that resolves with the window utilities instance.
     */
    windowUtils: () => new Promise((resolver) => resolver($windowUtils)),

    /**
     * Resolves the intersection observer utilities instance.
     * @returns {Promise} A promise that resolves with the intersection observer utilities instance.
     */
    intersectionUtils: () => new Promise((resolver) => resolver($observer)),

    /**
     * Resolves the lifecycle utility.
     * @returns {Promise} A promise that resolves with the lifecycle utility.
     */
    lifecycle: () => new Promise((resolver) => resolver(lifecycle)),

    /**
     * Loads the Neat background utility from FireCMS.
     * @returns {Promise} A promise that resolves with the Neat module.
     */
    background: () => import("@firecms/neat"),

    /**
     * Resolves the animated routes instance.
     * @returns {Promise} A promise that resolves with the animated routes instance.
     */
    animatedRoutes: () => new Promise((resolver) => resolver(AnimatedRoutes())),

    /**
     * Loads a smooth 3D tilt javascript library forked from Tilt.js (jQuery version).
     * @returns {Promise} A promise that resolves with the tilt module.
     */
    vanillaTilt: () => import("vanilla-tilt"),

    /**
     * Loads the VFX-JS.
     * @returns {Promise} A promise that resolves with the VFX module.
     */
    vfx: () => import("@vfx-js/core"),

    /**
     * Loads the maska class. https://beholdr.github.io/maska/v3/?ref=console.dev#/
     * @returns {Promise} A promise that resolves with the maska module.
     */
    maska: () => import("maska"),

    /**
     * Loads the tailwind intersect Observer class.
     * @returns {Promise} A promise that resolves with the tailwind intersect observer module.
     */
    tailwindIntersect: () =>
      new Promise(async (resolve) => {
        const { Observer } = await import("tailwindcss-intersect");

        async function docReady(fn) {
          await detectHeadChanges();

          if (hasRequiredElements()) {
            $async.setTimeout(fn, 1);
            return;
          }

          await new Promise((resolve) => $async.setTimeout(resolve, 1));

          return docReady(fn);

          function hasRequiredElements() {
            const intersectionElements = document.querySelectorAll(
              '[class^="intersect:"], [class*=" intersect:"]',
            );
            return Array.from(intersectionElements).length > 0;
          }

          function detectHeadChanges(timeout = 500) {
            return new Promise((resolveHead) => {
              const head = document.head;
              let timer;

              const callback = function (_, observer) {
                $async.clearTimeout(timer);
                timer = $async.setTimeout(() => {
                  observer.disconnect();
                  resolveHead();
                }, timeout);
              };

              const observer = new MutationObserver(callback);

              observer.observe(head, {
                childList: true,
                attributes: true,
                characterData: true,
                subtree: true,
              });

              timer = $async.setTimeout(() => {
                observer.disconnect();
                resolveHead();
              }, timeout);
            });
          }
        }

        await docReady(function () {
          resolve(Observer);
          Observer.start();
        });
      }),

    /**
     * Creates an instance of a SSE client.
     * @returns {Promise} A promise that resolves with the SSE client.
     */
    createSseClient: (dotnetContext) => new SseClient(dotnetContext),

    /**
     * Loads the Glide.js package.
     * @returns {Promise} A promise that resolves with the Glide module.
     */
    glide: async () => {
      const glide = await import("@glidejs/glide");
      return glide.default;
    },

    /**
     * Loads the ViewTransition utility.
     */
    viewTransition: () => import("./utils/ViewTransition.js"),

    /**
     * Loads HTMLToJson parser
     */
    html2Json: async () => {
      const { HTMLToJSON } = await import("html-to-json-parser");
      console.log(HTMLToJSON);
      return HTMLToJSON;
    },

    /**
     * Loads JSONToHTML parser
     */
    json2Html: async () => {
      const { JSONToHTML } = await import("html-to-json-parser");
      return JSONToHTML;
    },

    /**
     * Loads the autoComplete package.
     * @returns {Promise} A promise that resolves with the autoComplete module.
     */
    autoComplete: async () => {
      const autoComplete = await import("@tarekraafat/autocomplete.js");
      return autoComplete.default;
    },

    /**
     * Loads the ContentAnchoring js.
     * @returns {Promise} A promise that resolves with the autoComplete module.
     */
    contentAnchoring: async () => {
      const ContentAnchoring = await import("./utils/ContentAnchoring.js");
      return new ContentAnchoring();
    },

    /**
     * Loads the panzoom package.
     * @returns {Promise} A promise that resolves with the panzoom module.
     */
    panzoom: async () => {
      const panzoom = await import("@panzoom/panzoom");
      return panzoom.default;
    },
  };
}
